import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type { MetaAronFinding, MetaAronRun } from "../types";

const RUNS = "/aron/meta/runs/";
const FINDINGS = "/aron/meta/findings/";

export const aronMetaKeys = {
  all: ["aron", "meta"] as const,
  runs: ["aron", "meta", "runs"] as const,
  findings: (kind?: string, status?: string) =>
    ["aron", "meta", "findings", kind ?? "all", status ?? "all"] as const,
};

export function useMetaRuns() {
  return useQuery<MetaAronRun[]>({
    queryKey: aronMetaKeys.runs,
    queryFn: async () => {
      const r = await apiClient.get<{ data: MetaAronRun[] }>(`${RUNS}?page_size=20`);
      return r.data.data;
    },
    staleTime: 30_000,
  });
}

export function useMetaFindings(kind?: string, status?: string) {
  return useQuery<MetaAronFinding[]>({
    queryKey: aronMetaKeys.findings(kind, status),
    queryFn: async () => {
      const params = new URLSearchParams({ page_size: "200" });
      if (kind) params.set("kind", kind);
      if (status) params.set("status", status);
      const r = await apiClient.get<{ data: MetaAronFinding[] }>(
        `${FINDINGS}?${params.toString()}`,
      );
      return r.data.data;
    },
    staleTime: 30_000,
  });
}

function useInvalidateMeta() {
  const qc = useQueryClient();
  // Findings act on knowledge notes too — refresh both panels.
  return () => {
    qc.invalidateQueries({ queryKey: aronMetaKeys.all });
    qc.invalidateQueries({ queryKey: ["aron", "knowledge-notes"] });
  };
}

// Accept a KNOWLEDGE finding → promotes its PENDING note to ACTIVE.
export function useAcceptFinding() {
  const invalidate = useInvalidateMeta();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await apiClient.post<{ data: MetaAronFinding }>(`${FINDINGS}${id}/accept/`, {});
      return r.data.data;
    },
    onSuccess: invalidate,
  });
}

// Dismiss a finding → rejects a linked PENDING knowledge note (a DevIssue is left alone).
export function useDismissFinding() {
  const invalidate = useInvalidateMeta();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await apiClient.post<{ data: MetaAronFinding }>(`${FINDINGS}${id}/dismiss/`, {});
      return r.data.data;
    },
    onSuccess: invalidate,
  });
}
